import { useEffect, useMemo, useState } from "react";
import { Button, Card, Input, message, Select, Space, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import PvTable from "../../components/Table/PvTable";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import useFsrAdminService from "../../services/FsrAdmin/fsrAdminService";

const FOS_OPTIONS = [
  { label: "FEd", value: "Faculty of Education (FEd)" },
  { label: "FICS", value: "Faculty of Information and Communication Studies (FICS)" },
  { label: "FMDS", value: "Faculty of Management and Development Studies (FMDS)" },
  { label: "GE", value: "General Education (GE)" },
];

const CourseSection = () => {
  const {
    getCourses,
    courseListLoading,
    courseListResponse,
    createCourse,
    updateCourse,
    courseSaveLoading,
    deleteCourse,
  } = useFsrAdminService();

  const [courseNumber, setCourseNumber] = useState("");
  const [courseTitle, setCourseTitle] = useState("");
  const [fos, setFos] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableData = useMemo(
    () =>
      (courseListResponse?.data ?? courseListResponse ?? []).map(
        (item, index) => ({
          key: index + 1,
          ...item,
        })
      ),
    [courseListResponse]
  );

  const resetForm = () => {
    setEditingId(null);
    setCourseNumber("");
    setCourseTitle("");
    setFos(null);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setCourseNumber(record.course_number);
    setCourseTitle(record.course_title);
    setFos(record.fos ?? null);
  };

  const handleSave = async () => {
    if (!courseNumber || !courseTitle || !fos) {
      message.error("Please enter a course code, course title, and select a Faculty.");
      return;
    }

    try {
      if (editingId) {
        await updateCourse(editingId, courseNumber, courseTitle, fos);
      } else {
        await createCourse(courseNumber, courseTitle, fos);
      }
      message.success("Course saved.");
      resetForm();
      getCourses();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save course.";
      message.error(msg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCourse(id);
      message.success("Course deleted.");
      getCourses();
    } catch {
      message.error("Failed to delete course.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "key", width: 60 },
    {
      title: "Course Code",
      dataIndex: "course_number",
      key: "course_number",
      searchable: true,
    },
    {
      title: "Course Title",
      dataIndex: "course_title",
      key: "course_title",
      searchable: true,
    },
    {
      title: "Faculty",
      dataIndex: "fos",
      key: "fos",
      filters: FOS_OPTIONS.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      onFilter: (value, record) => record.fos === value,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <ConfirmDelete
            isService
            isIcon
            buttonText="Delete"
            onDeleted={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card title="Courses">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Space wrap align="start">
          <Input
            placeholder="Course Code"
            value={courseNumber}
            onChange={(e) => setCourseNumber(e.target.value)}
            style={{ width: 200 }}
          />
          <Input
            placeholder="Course Title"
            value={courseTitle}
            onChange={(e) => setCourseTitle(e.target.value)}
            style={{ width: 280 }}
          />
          <Select
            placeholder="Faculty"
            value={fos}
            onChange={setFos}
            options={FOS_OPTIONS}
            style={{ width: 160 }}
          />
          <Button
            type="primary"
            style={{ background: "#00573F" }}
            loading={courseSaveLoading}
            onClick={handleSave}
          >
            {editingId ? "Save" : "Add"}
          </Button>
          {editingId && <Button onClick={resetForm}>Cancel</Button>}
        </Space>

        <PvTable
          data={tableData}
          columns={columns}
          loading={courseListLoading}
          onRowClick={() => {}}
        />
      </Space>
    </Card>
  );
};

export default CourseSection;
